import { BooleanInput } from '@angular/cdk/coercion';
import {
  afterNextRender,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { NgpFocusVisible, NgpHover } from 'ng-primitives/interactions';
import { ChangeFn, provideValueAccessor, TouchedFn } from 'ng-primitives/utils';
import { LucideDynamicIcon } from '@lucide/angular';
import { AvatarComponent, AvatarVariant } from '../avatar/avatar.component';
import { TextDirective } from '../../typography/text.directive';
import { JoiiIcon } from '../../icons/lucide/icon';

export type SelectionVariant = 'text-only' | 'avatar' | 'icon';
export type SelectionSize = 'sm' | 'lg';

@Component({
  selector: 'joii-selection',
  imports: [AvatarComponent, LucideDynamicIcon, TextDirective],
  providers: [provideValueAccessor(SelectionComponent)],
  templateUrl: './selection.component.html',
  styleUrl: './selection.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [{ directive: NgpHover }, { directive: NgpFocusVisible }],
  host: {
    '[attr.data-selectable]': 'selectable()',
    '[attr.data-size]': 'size()',
    '[attr.data-variant]': 'variant()',
    '[attr.data-selected]': 'isSelected() ? "true" : "false"',
    '[attr.data-pressed]': 'isPressed() ? "true" : null',
    '[attr.data-disabled]': 'isDisabled() ? "true" : null',
    '[attr.data-full-width]': 'fullWidth()',
    '[attr.role]': 'hostRole()',
    '[attr.aria-checked]': 'selectable() ? isSelected() : null',
    '[attr.aria-current]': 'selectable() ? null : isSelected() ? "true" : null',
    '[attr.aria-disabled]': 'isDisabled()',
    '[attr.tabindex]': 'isDisabled() ? -1 : 0',
    '(click)': 'onHostClick($event)',
    '(keydown)': 'onKeydown($event)',
    '(keyup)': 'onKeyup($event)',
    '(pointerdown)': 'onPointerDown($event)',
    '(pointerup)': 'onPointerUp()',
    '(pointercancel)': 'onPointerUp()',
    '(pointerleave)': 'onPointerUp()',
    '(focusout)': 'onTouched?.()',
  },
})
export class SelectionComponent implements ControlValueAccessor {
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  readonly size = input<SelectionSize>('lg');
  readonly variant = input<SelectionVariant>('text-only');
  readonly label = input.required<string>();
  readonly value = input.required<unknown>();
  readonly disabled = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });
  readonly fullWidth = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });
  readonly selectable = input<boolean, BooleanInput>(true, {
    transform: booleanAttribute,
  });
  readonly avatarVariant = input<AvatarVariant | string>('dog');
  readonly avatarSrc = input<string>('');
  readonly icon = input<JoiiIcon>();

  readonly selectionClick = output<unknown>();

  protected readonly hostRole = computed(() => (this.selectable() ? 'radio' : 'menuitem'));
  protected readonly labelTextSize = computed(() => (this.size() === 'sm' ? 5 : 6));
  protected readonly avatarSize = computed(() => (this.size() === 'sm' ? '46px' : '52px'));
  protected readonly iconSize = computed(() => (this.size() === 'sm' ? 24 : 28));

  private readonly selectedValue = signal<unknown>(undefined);
  private readonly formDisabled = signal(false);
  private readonly pressed = signal(false);

  protected readonly isSelected = computed(() => Object.is(this.selectedValue(), this.value()));
  protected readonly isPressed = computed(() => this.pressed());
  protected readonly isDisabled = computed(() => this.disabled() || this.formDisabled());

  private onChange?: ChangeFn<unknown>;
  protected onTouched?: TouchedFn;

  constructor() {
    afterNextRender(() => {
      const ngControl = this.injector.get(NgControl, null, { self: true, optional: true });
      const control = ngControl?.control;
      if (!control) {
        return;
      }

      this.selectedValue.set(control.value);
      control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
        this.selectedValue.set(value);
      });
    });
  }

  writeValue(value: unknown): void {
    this.selectedValue.set(value);
  }

  registerOnChange(fn: ChangeFn<unknown>): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: TouchedFn): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled.set(isDisabled);
  }

  protected onHostClick(event: Event): void {
    if (this.isDisabled()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (!this.selectable()) {
      this.selectionClick.emit(this.value());
      return;
    }

    this.select();
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (this.isDisabled()) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();

      if (!this.selectable()) {
        this.pressed.set(true);
        this.selectionClick.emit(this.value());
        return;
      }

      this.select();
    }
  }

  protected onKeyup(event: KeyboardEvent): void {
    if (this.isDisabled() || this.selectable()) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      this.pressed.set(false);
    }
  }

  protected onPointerDown(event: PointerEvent): void {
    if (this.isDisabled() || this.selectable() || event.button !== 0) {
      return;
    }

    this.pressed.set(true);
  }

  protected onPointerUp(): void {
    this.pressed.set(false);
  }

  private select(): void {
    this.onChange?.(this.value());
    this.onTouched?.();
  }
}
